/**
 * CreateMeetingModal — create or edit a Meeting header.
 *
 * Create: pass projectId. Edit: also pass meetingId (loads the record from ctx.meetingCache,
 * falling back to a fetch). Captures the header fields (Type / Date / End / Location / Phase /
 * Attendees / Consultants); threads + activities are managed in the meeting-detail view, not here.
 *
 * Calls services/meetings.js (createMeeting / updateMeeting). On success → onSaved().
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { searchContacts, getContactNamesByIds } from '../services/contacts.js'
import {
  createMeeting, updateMeeting, getMeetingById, refreshMeetingAgenda,
  MEETING_TYPES, MEETING_LOCATIONS,
} from '../services/meetings.js'
import { alertDialog } from './Dialog.jsx'

// Meetings.Phase singleSelect choices (phase-NAME values, from the Airtable schema)
const PHASE_OPTIONS = [
  'Design Consultation', 'Pre-design & Planning', 'Schematic Design', 'Discretionary Approvals',
  'Design Development', 'Construction Documents', 'Permitting', 'Architectural Detailing',
  'Interior Architecture', 'Bidding & Negotiation', 'Construction Support',
]

// Consultants Involved multipleSelects choices (from the Airtable schema)
const CONSULTANT_OPTIONS = [
  'Civil Engineer', 'Landscape Architect', 'Structural Engineer', 'Mechanical Engineer',
  'Electrical Engineer', 'Interior Designer', 'Energy Consultant', 'Lighting Designer', 'Kitchen Designer',
]

/* ISO datetime → value for <input type="datetime-local"> (local wall-clock, no seconds). */
function toLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
/* datetime-local value → ISO string (or '' ). */
function fromLocalInput(v) {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d) ? '' : d.toISOString()
}

export default function CreateMeetingModal({ projectId, meetingId = null, presetType = null, zIndex, onClose, onSaved }) {
  const isEdit = !!meetingId
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving]   = useState(false)

  const [type, setType]         = useState(presetType || 'OAC')
  const [date, setDate]         = useState('')
  const [end, setEnd]           = useState('')
  const [location, setLocation] = useState('')
  const [phase, setPhase]       = useState('')
  const [consultants, setConsultants] = useState([])
  const [attendees, setAttendees] = useState([]) // [{ id, label }]

  // Attendee typeahead
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const searchTimer = useRef(null)

  // Load existing meeting for edit
  useEffect(() => {
    if (!isEdit) return
    let cancelled = false
    const cached = (ctx.meetingCache || []).find(m => m.id === meetingId)
    const apply = (rec) => {
      if (cancelled || !rec) return
      const f = rec.fields || {}
      setType(f['Type'] || 'OAC')
      setDate(toLocalInput(f['Meeting Date']))
      setEnd(toLocalInput(f['Meeting End']))
      setLocation(f['Location'] || '')
      setPhase(f['Phase'] || '')
      setConsultants(f['Consultants Involved'] || [])
      // Resolve EVERY attendee's name from Contacts (the PDF's source). The {Name (from Team
      // Attendees)} lookup only covers team members and isn't index-aligned with the full Attendees
      // list, which is what left client attendees showing the "Attendee" placeholder.
      const ids = f['Attendees'] || []
      setAttendees(ids.map(id => ({ id, label: 'Attendee' })))
      setLoading(false)
      if (ids.length) {
        getContactNamesByIds(ids).then(map => {
          if (cancelled) return
          setAttendees(ids.map(id => ({ id, label: map[id] || 'Attendee' })))
        })
      }
    }
    if (cached) apply(cached)
    else getMeetingById(meetingId).then(rec => apply(rec)).catch(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [isEdit, meetingId])

  // Debounced contact search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    const term = q.trim()
    if (term.length < 2) { setResults([]); return }
    setSearching(true)
    searchTimer.current = setTimeout(() => {
      searchContacts(term)
        .then(r => setResults((r.contacts || []).slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false))
    }, 220)
    return () => searchTimer.current && clearTimeout(searchTimer.current)
  }, [q])

  function addAttendee(c) {
    if (attendees.some(a => a.id === c.id)) return
    const label = (c.fields && (c.fields['Name'] || [c.fields['First Name'], c.fields['Last Name']].filter(Boolean).join(' '))) || 'Contact'
    setAttendees(prev => [...prev, { id: c.id, label }])
    setQ(''); setResults([])
  }
  function removeAttendee(id) { setAttendees(prev => prev.filter(a => a.id !== id)) }
  function toggleConsultant(c) {
    setConsultants(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])
  }

  async function save() {
    if (!type) { await alertDialog({ title: 'Type required', message: 'Pick a meeting type.' }); return }
    if (!date) { await alertDialog({ title: 'Date required', message: 'Pick a meeting date and time.' }); return }
    if (type === 'Consultant Coordination' && consultants.length === 0) {
      await alertDialog({ title: 'Consultant required', message: 'A consultant coordination meeting must involve at least one consultant — that scopes which prior threads carry forward.' }); return
    }
    const consultantsOut = type === 'Consultant Coordination' ? consultants : []
    setSaving(true)
    try {
      if (isEdit) {
        const fields = {
          'Type': type,
          'Meeting Date': fromLocalInput(date),
          'Meeting End': end ? fromLocalInput(end) : null,
          'Location': location || null,
          'Phase': phase || null,
          'Attendees': attendees.map(a => a.id),
          'Consultants Involved': consultantsOut,
        }
        await updateMeeting(meetingId, fields)
        // Attendees may have changed: their open threads join the agenda (only before it's held).
        try { await refreshMeetingAgenda(meetingId) } catch (_) { /* non-fatal */ }
      } else {
        const rec = await createMeeting(projectId, {
          type,
          meetingDate: fromLocalInput(date),
          meetingEnd: end ? fromLocalInput(end) : null,
          location: location || null,
          phase: phase || null,
          attendees: attendees.map(a => a.id),
          consultants: consultantsOut,
          creatorMemberId: (ctx.currentMember && ctx.currentMember.id) || wf.currentMemberId || null,
        })
        // The agenda fills itself: every open thread that involves someone attending (every open
        // thread for an internal meeting). It keeps refreshing until the meeting is held.
        try { if (rec && rec.id) await refreshMeetingAgenda(rec.id) } catch (_) { /* non-fatal; refreshed again when opened */ }
      }
      onSaved && onSaved()
    } catch (e) {
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save the meeting.' })
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div style={zIndex ? { ...overlay, zIndex } : overlay} onClick={onClose}>
      <div data-modal-id="CreateMeetingModal" style={card} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <div style={head}>
          <span style={title}>{isEdit ? 'Edit meeting' : 'New meeting'}</span>
          <button onClick={onClose} style={xBtn}>×</button>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: 'var(--aj-text-muted)', fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={body}>
            <Field label="Meeting type">
              {presetType && !isEdit ? (
                <div style={{ ...input, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', display: 'flex', alignItems: 'center' }}>{type}</div>
              ) : (
                <select value={type} onChange={e => setType(e.target.value)} style={input}>
                  {MEETING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </Field>

            <div style={{ display: 'flex', gap: 10 }}>
              <Field label="Date & time" grow>
                <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} style={input} />
              </Field>
              <Field label="End (optional)" grow>
                <input type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} style={input} />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Field label="Location" grow>
                <select value={location} onChange={e => setLocation(e.target.value)} style={input}>
                  <option value="">—</option>
                  {MEETING_LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Phase" grow>
                <select value={phase} onChange={e => setPhase(e.target.value)} style={input}>
                  <option value="">—</option>
                  {PHASE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Attendees">
              {attendees.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {attendees.map(a => (
                    <span key={a.id} style={chip}>
                      {a.label}
                      <button onClick={() => removeAttendee(a.id)} style={chipX}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <input
                value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search contacts to add…" style={input}
              />
              {(searching || results.length > 0) && (
                <div style={dropdown}>
                  {searching && <div style={{ padding: '6px 10px', color: 'var(--aj-text-muted)', fontSize: 12 }}>Searching…</div>}
                  {results.map(c => {
                    const f = c.fields || {}
                    const name = f['Name'] || [f['First Name'], f['Last Name']].filter(Boolean).join(' ') || 'Contact'
                    const sub = f['Email'] || f['Contact Type'] || ''
                    return (
                      <button key={c.id} onClick={() => addAttendee(c)} style={dropItem}>
                        <span style={{ color: 'var(--aj-text)' }}>{name}</span>
                        {sub && <span style={{ color: 'var(--aj-text-muted)', fontSize: 11, marginLeft: 8 }}>{sub}</span>}
                      </button>
                    )
                  })}
                </div>
              )}
            </Field>

            {/* Consultants apply only to Consultant Coordination meetings — they scope which prior
                threads carry forward. Other meeting types don't show this. */}
            {type === 'Consultant Coordination' && (
              <Field label="Consultants involved (required)">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CONSULTANT_OPTIONS.map(c => {
                    const on = consultants.includes(c)
                    return (
                      <button key={c} onClick={() => toggleConsultant(c)} style={{ ...pill, ...(on ? pillOn : {}) }}>{c}</button>
                    )
                  })}
                </div>
              </Field>
            )}
          </div>
        )}

        <div style={foot}>
          <button onClick={onClose} style={ghostBtn} disabled={saving}>Cancel</button>
          <button onClick={save} style={primaryBtn} disabled={saving || loading}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create meeting'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function Field({ label, children, grow }) {
  return (
    <div style={{ marginBottom: 12, ...(grow ? { flex: 1 } : {}) }}>
      <div style={fieldLabel}>{label}</div>
      {children}
    </div>
  )
}

/* ── styles ─────────────────────────────────────────────────────────────── */
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflowY: 'auto' }
const card = { width: '100%', maxWidth: 520, background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', overflow: 'visible' }
const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border)' }
const title = { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' }
const xBtn = { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' }
const body = { padding: '16px 18px' }
const foot = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border)' }
const fieldLabel = { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 }
const input = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const dropdown = { position: 'relative', marginTop: 4, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxShadow: '0 6px 18px rgba(0,0,0,0.14)', maxHeight: 220, overflowY: 'auto', zIndex: 2 }
const dropItem = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 10px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, borderBottom: '0.5px solid var(--aj-border)' }
const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--aj-bg-muted)', borderRadius: 12, padding: '2px 4px 2px 10px', fontSize: 12, color: 'var(--aj-text)' }
const chipX = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 14, lineHeight: 1 }
const pill = { background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '4px 11px', fontSize: 12, cursor: 'pointer', color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif' }
const pillOn = { background: '#07272D', color: '#fff', borderColor: '#07272D' }
const primaryBtn = { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' }
const ghostBtn = { background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }
